import { useEffect, useState } from "react";
import { usePromptSettings, useUpdatePromptSettings } from "@/hooks/usePromptSettings";
import { Card, Button, PageHeader, Toggle, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

const SettingsPrompt = () => {
  const { data, isLoading } = usePromptSettings();
  const updateMutation = useUpdatePromptSettings();

  const [adminInstructions, setAdminInstructions] = useState("");
  const [referenceData, setReferenceData] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.config) {
      setAdminInstructions(data.config.adminInstructions || "");
      setReferenceData(data.config.referenceData || "");
      setEnabled(data.config.enabled || false);
    }
  }, [data]);

  const limits = data?.limits;
  const defaultReferenceData = data?.defaults?.referenceData || "";

  const adminInstructionsBytes = new Blob([adminInstructions]).size;
  const referenceDataBytes = new Blob([referenceData]).size;
  const adminInstructionsOverLimit =
    limits && adminInstructionsBytes > limits.adminInstructionsMaxBytes;
  const referenceDataOverLimit =
    limits && referenceDataBytes > limits.referenceDataMaxBytes;
  const enabledWithoutContent = enabled && !referenceData.trim();

  const canSubmit =
    !adminInstructionsOverLimit &&
    !referenceDataOverLimit &&
    !enabledWithoutContent &&
    !updateMutation.isPending;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await updateMutation.mutateAsync({
        adminInstructions,
        referenceData,
        enabled,
      });
    } catch (err: any) {
      const message =
        err?.detail || err?.message || "Failed to save system prompt settings.";
      setError(String(message));
    }
  };

  const handleLoadDefault = () => {
    setReferenceData(defaultReferenceData);
  };

  const handleResetAll = async () => {
    if (!window.confirm("Reset to defaults? This clears both fields and disables the override.")) {
      return;
    }
    setError(null);
    setAdminInstructions("");
    setReferenceData("");
    setEnabled(false);
    try {
      await updateMutation.mutateAsync({
        adminInstructions: "",
        referenceData: "",
        enabled: false,
      });
    } catch (err: any) {
      setError(String(err?.detail || err?.message || "Failed to reset."));
    }
  };

  return (
    <div className="page">
      <PageHeader
        label="Settings"
        title="System Prompt"
        description="Customize the assistant's behavior and reference data. The audited security policies stay in code and are not editable here."
      />

      <form onSubmit={handleSubmit}>
        <div className="stack">
          {/* Section A — Admin Instructions */}
          <Card
            title="Admin Instructions"
            description="Extra rules appended to every chat. Useful for sign-offs, tone overrides, or temporary advisories."
            actions={
              <Badge variant="default">
                <Icon name="shield" />
                Applies to all roles
              </Badge>
            }
          >
            <div className="fg">
              <div className="lbl-row">
                <label className="fl">Instructions</label>
                <span className={adminInstructionsOverLimit ? "cnt warn" : "cnt"}>
                  {adminInstructionsBytes.toLocaleString()} bytes
                  {limits ? ` / ${limits.adminInstructionsMaxBytes.toLocaleString()}` : ""}
                </span>
              </div>
              <textarea
                className="ta mono"
                rows={8}
                value={adminInstructions}
                onChange={(e) => setAdminInstructions(e.target.value)}
                placeholder="e.g. Always sign off with: 'Need anything else? Reply STOP to unsubscribe.'"
              />
            </div>
            <div className="al i">
              <svg viewBox="0 0 24 24">
                <use href="#i-info" />
              </svg>
              <div className="cnt-x">
                Per-request playground overrides still take precedence over this saved value.
              </div>
            </div>
          </Card>

          {/* Section B — Reference Data Override */}
          <Card
            title="Reference Data Override"
            description="Replace the built-in facts block (packages, bonuses, customer support, policies, etc.). When the toggle is off, the in-code defaults apply."
            actions={<Toggle checked={enabled} onChange={setEnabled} />}
          >
            {!enabled && (
              <div className="al i" style={{ marginBottom: 14 }}>
                <svg viewBox="0 0 24 24">
                  <use href="#i-info" />
                </svg>
                <div className="cnt-x">
                  Currently using the built-in reference data. Toggle on to override.
                </div>
              </div>
            )}

            {enabled && enabledWithoutContent && (
              <div className="al w" style={{ marginBottom: 14 }}>
                <svg viewBox="0 0 24 24">
                  <use href="#i-alert" />
                </svg>
                <div className="cnt-x">
                  The override is enabled but the body is empty. Either disable the override
                  or paste content below before saving.
                </div>
              </div>
            )}

            <div className="fg">
              <div className="lbl-row">
                <label className="fl">Reference Data</label>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className={referenceDataOverLimit ? "cnt warn" : "cnt"}>
                    {referenceDataBytes.toLocaleString()} bytes
                    {limits ? ` / ${limits.referenceDataMaxBytes.toLocaleString()}` : ""}
                  </span>
                  <button
                    type="button"
                    className="act-txt"
                    onClick={handleLoadDefault}
                    disabled={!enabled || !defaultReferenceData}
                  >
                    Load default
                  </button>
                </span>
              </div>
              <textarea
                className="ta mono"
                rows={24}
                value={referenceData}
                onChange={(e) => setReferenceData(e.target.value)}
                disabled={!enabled}
                placeholder={
                  enabled
                    ? "Paste the reference data block. Use 'Load default' below to start from the in-code copy."
                    : "Toggle the override on to edit this block."
                }
                style={!enabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
              />
            </div>
          </Card>
        </div>

        {error && (
          <div className="al e" style={{ marginTop: 14 }}>
            <svg viewBox="0 0 24 24">
              <use href="#i-alert" />
            </svg>
            <div className="cnt-x">{error}</div>
          </div>
        )}

        {/* Footer / Save bar */}
        <div className="savebar">
          <span className="meta">
            {isLoading ? (
              "Loading..."
            ) : data?.config?.updatedAt ? (
              <>
                Last updated {new Date(data.config.updatedAt).toLocaleString()}
                {data.config.updatedBy ? ` by ${data.config.updatedBy}` : ""}
              </>
            ) : (
              "No saved overrides yet."
            )}
          </span>
          <div className="r">
            <Button type="button" variant="ghost" onClick={handleResetAll}>
              Reset to defaults
            </Button>
            <Button type="submit" loading={updateMutation.isPending} disabled={!canSubmit}>
              Save Settings
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsPrompt;
